import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { PROFESSIONS, NICHES } from '../constants';
import { categorizeNiche } from '../utils/nicheUtils';

export interface ProfessionItem {
  name: string;
  niche: string;
  search_terms?: string[];
  isSmartMatch?: boolean;
  score?: number;
}

const normalizeText = (text: string): string => {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

export function useProfessions() {
  const [professions, setProfessions] = useState<ProfessionItem[]>(PROFESSIONS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let ignore = false;

    const fetchProfessions = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('professions')
          .select('name, niche, search_terms');

        if (!ignore && !error && data && data.length > 0) {
          // Merge database data with static constants fallback (ensuring no duplicates)
          const dbMap = new Map<string, ProfessionItem>();
          
          // Seed static fallback first
          PROFESSIONS.forEach(p => dbMap.set(p.name, p));

          // Override / add with DB entries
          data.forEach((p: ProfessionItem) => dbMap.set(p.name, p));

          setProfessions(Array.from(dbMap.values()));
        }
      } catch (err) {
        console.warn('Erro ao carregar profissões do banco (usando fallback estático):', err);
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    };

    fetchProfessions();

    return () => {
      ignore = true;
    };
  }, []);

  /**
   * Smart Search Engine with Relevance Scoring
   */
  const searchProfessions = useCallback((query: string, maxResults = 12): ProfessionItem[] => {
    const normQuery = normalizeText(query);
    if (!normQuery || normQuery.length < 2) return [];

    const scoredResults: { item: ProfessionItem; score: number }[] = [];

    professions.forEach(p => {
      const normName = normalizeText(p.name);
      const normNiche = normalizeText(p.niche);
      let highestScore = 0;

      // 1. Direct Name Matches (Highest Priority)
      if (normName === normQuery) {
        highestScore = 110;
      } else if (normName.startsWith(normQuery)) {
        highestScore = 100;
      } else if (normName.includes(` ${normQuery}`) || normName.includes(`/${normQuery}`) || normName.includes(`(${normQuery}`)) {
        highestScore = 90;
      } else if (normName.includes(normQuery)) {
        highestScore = 80;
      }

      // 2. Search Terms / Synonyms Matches
      if (p.search_terms && p.search_terms.length > 0) {
        for (const term of p.search_terms) {
          const normTerm = normalizeText(term);
          if (!normTerm) continue;

          let termScore = 0;
          if (normTerm === normQuery) {
            termScore = 75;
          } else if (normTerm.startsWith(normQuery)) {
            termScore = 70;
          } else if (normTerm.includes(` ${normQuery}`)) {
            termScore = 65;
          } else if (normTerm.includes(normQuery)) {
            termScore = 55;
          }

          if (termScore > highestScore) {
            highestScore = termScore;
          }
        }
      }

      // 3. Niche Matches (Lowest Priority, but still matches)
      if (highestScore < 50) {
        if (normNiche.startsWith(normQuery)) {
          highestScore = 45;
        } else if (normNiche.includes(normQuery)) {
          highestScore = 35;
        }
      }

      if (highestScore > 0) {
        scoredResults.push({ item: p, score: highestScore });
      }
    });

    // Sort by Score DESC, then Name ASC
    scoredResults.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return a.item.name.localeCompare(b.item.name, 'pt-BR');
    });

    let finalResults = scoredResults.map(r => ({ ...r.item, score: r.score }));

    // Smart Match Fallback: if query categorizes into a specific niche not represented in top results
    const identifiedNiche = categorizeNiche(query);
    const hasIdentifiedNicheInTop = finalResults.slice(0, 5).some(r => r.niche === identifiedNiche);

    if (identifiedNiche && identifiedNiche !== NICHES[0] && !hasIdentifiedNicheInTop) {
      finalResults = [
        { name: identifiedNiche, niche: identifiedNiche, isSmartMatch: true, score: 30 },
        ...finalResults
      ];
    }

    return finalResults.slice(0, maxResults);
  }, [professions]);

  return {
    professions,
    isLoading,
    searchProfessions
  };
}
